import { useEffect, useState } from 'react';
import type { ActivityLog } from 'dico-types';
import { MonoIcon } from '@/components/ui/MonoIcon';
import { formatDistanceToNow } from 'date-fns';
import type { MonoIconType } from '@/components/ui/icon';
import { listTicketActivityLogs } from '@/lib/api/activity-log/activityLogApi';

function getEventIcon(eventType: string): MonoIconType {
  if (eventType === 'ticket.created') return 'Plus';
  if (eventType === 'ticket.status.changed') return 'CheckCircle';
  if (eventType === 'ticket.priority.changed') return 'Flag';
  if (eventType === 'ticket.assignee.added') return 'UserPlus';
  if (eventType === 'ticket.assignee.removed') return 'User';
  if (eventType === 'ticket.tag.added') return 'Tag';
  if (eventType === 'ticket.tag.removed') return 'Tag';
  if (eventType === 'ticket.title.changed') return 'Edit';
  if (eventType === 'ticket.description.changed') return 'FileText';
  if (eventType === 'ticket.team.changed') return 'User';
  if (eventType === 'ticket.project.changed') return 'Folder';
  if (eventType === 'ticket.response.draft.created') return 'Edit';
  if (eventType === 'ticket.response.published') return 'Send';
  if (eventType === 'ticket.response.failed') return 'AlertCircle';
  return 'HelpCircle';
}

function getEventIconColor(eventType: string): string {
  if (eventType === 'ticket.created') return 'text-green-500';
  if (eventType === 'ticket.response.published') return 'text-primary';
  if (eventType === 'ticket.response.failed') return 'text-destructive';
  if (eventType.includes('removed')) return 'text-orange-500';
  if (eventType.includes('added')) return 'text-blue-500';
  return 'text-muted-foreground';
}

interface ActivityLogItemProps {
  log: ActivityLog;
}

function ActivityLogItem({ log }: ActivityLogItemProps) {
  const IconComponent = getEventIcon(log.eventType);
  const iconColor = getEventIconColor(log.eventType);

  return (
    <div className="my-2 flex items-start gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 shrink-0 ${iconColor}`}>
            <MonoIcon type={IconComponent} className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-foreground text-sm font-medium">{log.eventTitle}</p>
            {log.eventDescription && (
              <p className="text-muted-foreground mt-0.5 text-xs">{log.eventDescription}</p>
            )}
            <p className="text-muted-foreground mt-1 text-xs">
              {formatDistanceToNow(new Date(log.createdAt), { addSuffix: true })}
              {log.actorName && ` • By ${log.actorName}`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

interface TicketActivityLogProps {
  workspaceId: string;
  ticketId: string;
}

export function TicketActivityLog({ workspaceId, ticketId }: TicketActivityLogProps) {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function loadLogs() {
      if (!workspaceId || !ticketId) return;

      setLoading(true);
      setError(null);

      try {
        const data = await listTicketActivityLogs(workspaceId, ticketId, { limit: 100 });
        if (mounted) {
          setLogs(data);
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setError('Failed to load activity history');
          setLoading(false);
        }
      }
    }

    loadLogs();

    return () => {
      mounted = false;
    };
  }, [workspaceId, ticketId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <MonoIcon type="Loader2" className="text-muted-foreground h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 text-center">
        <p className="text-destructive text-sm">{error}</p>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-muted-foreground text-sm">No activity yet</p>
      </div>
    );
  }

  return (
    <div className="divide-border divide-y rounded-lg border px-4 py-2">
      {logs.map((log) => (
        <ActivityLogItem key={log.id} log={log} />
      ))}
    </div>
  );
}
